import React, { useEffect, useRef, useCallback, useState } from 'react';
import Pickr from '@simonwep/pickr';
import '@simonwep/pickr/dist/themes/classic.min.css';
import type { Editor as TiptapEditor } from '@tiptap/react'

interface Props {
    editor: TiptapEditor;
}

const ColorPickerButton: React.FC<Props> = ({ editor }) => {
    const pickrRef = useRef<HTMLButtonElement>(null);
    const pickrInstance = useRef<any>(null); // Pickr インスタンスに公式の型が無いため any

    const [currentColor, setCurrentColor] = useState<string>('#000000');

    const convertRgbToHex = useCallback((color: string): string => {
        if (color) {
            return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/g, (_match, r, g, b) =>
                '#' +
                [r, g, b]
                    .map((v: string) =>
                        parseInt(v, 10).toString(16).padStart(2, '0').toUpperCase()
                    )
                    .join('')
            );
        }
        return '';
    }, []);

    const getCurrentEditorTextColor = useCallback(() => {
        const colorAttr = editor.getAttributes('textStyle').color;
        const hex = convertRgbToHex(colorAttr);
        return hex || '#000000';
    }, [editor.getAttributes('textStyle').color, convertRgbToHex]);

    useEffect(() => {
        setCurrentColor(getCurrentEditorTextColor());
    }, [getCurrentEditorTextColor]);

    const [color, setColor] = useState<string | null>(null)
    useEffect(() => {
        if (!editor) return

        const update = () => {
            setColor(editor.getAttributes('textStyle').color ?? null)
        }

        editor.on('selectionUpdate', update)
        update()

        return () => {
            editor.off('selectionUpdate', update)
        }
    }, [editor])

    useEffect(() => {
        if (pickrRef.current) {
            pickrInstance.current = Pickr.create({
                el: pickrRef.current,
                theme: 'classic',
                default: currentColor,
                components: {
                    preview: true,
                    opacity: false,
                    hue: true,
                    interaction: {
                        hex: false,
                        rgba: false,
                        input: true,
                        clear: true,
                        save: false,
                    },
                },
            });

            pickrInstance.current.on('change', (color: Pickr.HSVaColor) => {
                const hex = color.toHEXA().toString();
                editor.chain().focus().setColor(hex).run();
            });

            pickrInstance.current.on('clear', () => {
                editor.chain().focus().unsetColor().run();
            });

            pickrInstance.current.on('show', () => {
                const color = getCurrentEditorTextColor();
                pickrInstance.current.setColor(color);
            });
        }

        return () => {
            pickrInstance.current?.destroyAndRemove();
        };
    }, []);

    useEffect(() => {
        if (pickrInstance.current) {
            pickrInstance.current.setColor(currentColor);
        }
    }, [currentColor]);

    return <div>
        <button ref={pickrRef}>Pick a color</button>
    </div>;
};

export default ColorPickerButton;
